import { useState, useEffect, useRef, useCallback } from 'react'
import { compileLatex, CompileResult } from '../services/latexCompiler'

interface UseLatexCompilerOptions {
  debounceMs?: number
  autoCompile?: boolean
}

interface UseLatexCompilerReturn {
  compile: (source: string) => Promise<void>
  pdfUrl: string | null
  isCompiling: boolean
  error: string | null
  logs: string[]
}

export function useLatexCompiler(
  options: UseLatexCompilerOptions = {}
): UseLatexCompilerReturn {
  const { debounceMs = 1000, autoCompile = true } = options

  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [isCompiling, setIsCompiling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logs, setLogs] = useState<string[]>([])

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentPdfUrlRef = useRef<string | null>(null)

  // Cleanup blob URL on unmount or when replaced
  useEffect(() => {
    return () => {
      if (currentPdfUrlRef.current) {
        URL.revokeObjectURL(currentPdfUrlRef.current)
      }
    }
  }, [])

  const doCompile = useCallback(async (source: string) => {
    setIsCompiling(true)
    setError(null)

    const result: CompileResult = await compileLatex(source)

    if (result.success && result.pdf) {
      // Revoke old URL
      if (currentPdfUrlRef.current) {
        URL.revokeObjectURL(currentPdfUrlRef.current)
      }

      // Create new URL
      const newUrl = URL.createObjectURL(result.pdf)
      currentPdfUrlRef.current = newUrl
      setPdfUrl(newUrl)
      setLogs([])
    } else {
      setError(result.error || 'Compilation failed')
      setLogs(result.logs || [])
    }

    setIsCompiling(false)
  }, [])

  const compile = useCallback(
    async (source: string) => {
      if (!autoCompile) {
        await doCompile(source)
        return
      }

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        doCompile(source)
      }, debounceMs)
    },
    [autoCompile, debounceMs, doCompile]
  )

  return {
    compile,
    pdfUrl,
    isCompiling,
    error,
    logs,
  }
}
